// SEO-12 — the article body renderer for /resources.
//
// Deliberately NOT ui/Markdown: that renderer is tuned for Aira's streamed output (purple `text-ai`
// links, plain <a> for internal paths — a full document reload inside the SPA). Marketing prose
// needs router <Link>s in the brand accent, so the inline grammar here is a small, closed subset:
//
//   [label](/internal/path)   -> react-router Link (crawlable, SPA-navigated)
//   [label](https://host/…)   -> external anchor, new tab, rel=noopener, sr-only affordance
//   **bold**                  -> <strong>
//
// Every node is a React text node — no dangerouslySetInnerHTML, so the content module can never
// inject markup. Anything that is not one of the three tokens above renders as literal text.
import React from "react";
import { Link } from "react-router-dom";

const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\((?:\/[^)\s]*|https?:\/\/[^)\s]+)\))/g;
const LINK = /^\[([^\]]+)\]\((\/[^)\s]*|https?:\/\/[^)\s]+)\)$/;

const LINK_CLASS =
  "font-semibold text-accent-active underline decoration-current/30 underline-offset-2 hover:decoration-current rounded focus:outline-none focus-visible:ring-2 ring-primary";

/** Render one string of prose with the inline token grammar above. */
export function Inline({ text, keyPrefix = "t" }) {
  const value = String(text || "");
  const out = [];
  let last = 0;
  let index = 0;
  INLINE.lastIndex = 0;
  for (let m = INLINE.exec(value); m; m = INLINE.exec(value)) {
    if (m.index > last) out.push(value.slice(last, m.index));
    const token = m[0];
    const key = `${keyPrefix}-${index}`;
    index += 1;
    if (token.startsWith("**")) {
      out.push(<strong key={key} className="font-semibold text-ink">{token.slice(2, -2)}</strong>);
    } else {
      const parsed = LINK.exec(token);
      const [, label, href] = parsed;
      if (/^https?:\/\//i.test(href)) {
        out.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
            {label}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>,
        );
      } else {
        out.push(<Link key={key} to={href} className={LINK_CLASS}>{label}</Link>);
      }
    }
    last = m.index + token.length;
  }
  if (last < value.length) out.push(value.slice(last));
  return <>{out}</>;
}

function ListBlock({ block, ordered, idPrefix }) {
  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag
      className={
        ordered
          ? "mt-5 space-y-3.5 list-decimal marker:text-accent marker:font-semibold pl-5"
          : "mt-5 space-y-3.5 list-disc marker:text-accent pl-5"
      }
    >
      {block.items.map((item, i) => (
        <li key={`${idPrefix}-li-${i}`} className="text-[16px] leading-[1.75] text-ink-2 pl-1">
          <Inline text={item} keyPrefix={`${idPrefix}-li-${i}`} />
        </li>
      ))}
    </Tag>
  );
}

function Block({ block, idPrefix }) {
  if (block.t === "ul") return <ListBlock block={block} ordered={false} idPrefix={idPrefix} />;
  if (block.t === "ol") return <ListBlock block={block} ordered idPrefix={idPrefix} />;
  if (block.t === "note") {
    return (
      <aside className="mt-6 rounded-2xl border border-line bg-muted/60 px-5 py-4" data-testid="resource-note">
        {block.title ? (
          <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-accent">{block.title}</p>
        ) : null}
        <p className="mt-1.5 text-[15px] leading-[1.7] text-ink-2">
          <Inline text={block.text} keyPrefix={`${idPrefix}-note`} />
        </p>
      </aside>
    );
  }
  return (
    <p className="mt-5 text-[16px] leading-[1.8] text-ink-2 sm:text-[17px]">
      <Inline text={block.text} keyPrefix={`${idPrefix}-p`} />
    </p>
  );
}

/** The full article body: one <section> per authored section, each headed by an h2. */
export default function ResourceProse({ sections }) {
  return (
    <>
      {(sections || []).map((section) => (
        <section key={section.id} aria-labelledby={`resource-section-${section.id}`} className="mt-11 first:mt-8">
          <h2
            id={`resource-section-${section.id}`}
            className="font-heading text-[22px] font-bold leading-tight tracking-tight text-ink sm:text-[26px]"
          >
            {section.heading}
          </h2>
          {(section.blocks || []).map((block, i) => (
            <Block key={`${section.id}-${i}`} block={block} idPrefix={`${section.id}-${i}`} />
          ))}
        </section>
      ))}
    </>
  );
}
